import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
} from '@openzeppelin/ui-components';
import { cn } from '@openzeppelin/ui-utils';
import { Icon } from '@ascend/shared/components';
import { useWalletStore } from '../stores/useWalletStore';
import { CLIENT_MARGIN_ACCOUNTS, CLIENT_LENDING_POOLS, CLIENT_WALLET_BALANCES } from '../data/mockAdapter';

// ─── Types ──────────────────────────────────────────────────────────────────────

type HealthStatus = 'healthy' | 'at-risk' | 'liquidated';
type FacilityStatus = 'active' | 'paused';

interface CreditFacility {
  id: string;
  name: string;
  settlementTokens: string[];
  collateralTokens: string[];
  collateral: string;
  borrowed: string;
  healthFactor: string;
  status: HealthStatus;
  facilityStatus: FacilityStatus;
  tvl: string;
  utilization: string;
  interestRate: string;
  maxLTV: string;
}

interface MarginAccount {
  id: string;
  facilityId: string;
  facilityName: string;
  settlementTokens: string[];
  collateralTokens: string[];
  collateral: string;
  collateralAmount: string;
  collateralAmounts: Record<string, string>;
  borrowed: string;
  borrowedAmount: string;
  borrowedUsd: number;
  interestAccrued: string;
  interestAccruedUsd: number;
  interestRatePct: number;
  liquidationThresholdPct: number;
  maxLtvPct: number;
  collateralValueUsd: number;
  healthFactor: string;
  status: HealthStatus;
}

// ─── Mock Data Generators ───────────────────────────────────────────────────────

const FACILITY_NAMES = [
  'USDC Core Facility', 'ABN Yield Facility', 'SNG Stable Facility', 'High Risk Alt Facility',
  'Prime Credit Facility', 'Capital Lending Pool', 'Stability Reserve Facility', 'Growth Opportunity Pool',
  'APX Opportunity Fund', 'FMM Reserve Facility', 'MSM Income Pool', 'SBC Capital Facility',
  'Meridian Credit Facility', 'Horizon Yield Fund', 'Summit Lending Pool',
  'Pinnacle Reserve', 'Vertex Income Facility', 'Zenith Growth Fund',
  'Nova Stable Pool', 'Stellar Credit Facility', 'Quantum Yield Pool',
  'Atlas Lending Fund', 'Nexus Reserve Pool', 'Forge Capital Fund',
];

const SETTLEMENT_TOKEN_SETS: string[][] = [
  ['USDC'], ['USDC', 'USD1'], ['USDC', 'USDT'], ['USDC'],
  ['USDT', 'USD1'], ['USD1'], ['USDC', 'USDT', 'USD1'], ['USDC', 'USDT'],
  ['USDC', 'EURC'], ['USDC'], ['USDT'], ['USD1', 'USDC'],
  ['USDC', 'USDT', 'EURC'], ['USD1'], ['USDC', 'USDT'],
  ['USDC'], ['USDT', 'USD1'], ['EURC'],
  ['USDC', 'USDT'], ['USDC'], ['USD1', 'USDT'],
  ['USDC', 'USD1'], ['USDT'], ['USDC'],
];

const COLLATERAL_TOKEN_SETS: string[][] = [
  ['ABN', 'FMM'], ['SNG'], ['APX', 'MSM'], ['SBC'],
  ['ABN', 'SNG'], ['FMM', 'APX'], ['MSM', 'SBC'], ['ABN'],
  ['SNG', 'FMM'], ['APX'], ['MSM'], ['SBC', 'ABN'],
  ['SNG', 'APX'], ['FMM'], ['MSM', 'SBC'],
  ['ABN', 'APX'], ['SNG'], ['FMM', 'MSM'],
  ['SBC'], ['ABN', 'SNG'], ['APX', 'FMM'],
  ['MSM'], ['SBC', 'SNG'], ['ABN', 'FMM'],
];

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function generateCreditFacilities(): CreditFacility[] {
  const statuses: HealthStatus[] = ['healthy', 'healthy', 'healthy', 'at-risk', 'liquidated'];
  const facilityStatuses: FacilityStatus[] = ['active', 'active', 'active', 'active', 'paused'];

  return FACILITY_NAMES.map((name, i) => {
    const r = seededRandom(i + 1);
    const status = statuses[i % statuses.length];
    const fStatus = facilityStatuses[i % facilityStatuses.length];
    const collVal = Math.floor(r * 150000 + 5000);
    const borVal = status === 'healthy' ? Math.floor(collVal * r * 0.4) : Math.floor(collVal * r * 0.8);
    const hf = status === 'liquidated' ? '0.00' : (collVal / Math.max(borVal, 1)).toFixed(2);
    const tvl = (r * 25 + 0.5).toFixed(1);
    const util = (r * 80 + 10).toFixed(1);
    const rate = (r * 8 + 2).toFixed(1);
    const ltv = (r * 30 + 40).toFixed(0);

    return {
      id: `cf-${i + 1}`,
      name,
      settlementTokens: SETTLEMENT_TOKEN_SETS[i % SETTLEMENT_TOKEN_SETS.length],
      collateralTokens: COLLATERAL_TOKEN_SETS[i % COLLATERAL_TOKEN_SETS.length],
      collateral: `$ ${collVal.toLocaleString()}`,
      borrowed: `$ ${borVal.toLocaleString()}`,
      healthFactor: hf,
      status,
      facilityStatus: fStatus,
      tvl: `$${tvl}M`,
      utilization: `${util}%`,
      interestRate: `${rate}%`,
      maxLTV: `${ltv}%`,
    };
  });
}

const ALL_CREDIT_FACILITIES = generateCreditFacilities();
const ALL_MARGIN_ACCOUNTS: MarginAccount[] = CLIENT_MARGIN_ACCOUNTS.map((a) => ({
  id: a.accountId,
  facilityId: a.facilityId,
  facilityName: a.facilityName,
  settlementTokens: [a.settlementToken],
  collateralTokens: a.collateralTokens,
  collateral: a.collateralValue,
  collateralAmount: a.collateralAmountText,
  collateralAmounts: a.collateralAmounts,
  borrowed: a.borrowedValue,
  borrowedAmount: a.borrowedAmount,
  borrowedUsd: a.borrowedUsd,
  interestAccrued: a.interestAccrued,
  interestAccruedUsd: a.interestAccruedUsd,
  interestRatePct: a.interestRatePct,
  liquidationThresholdPct: a.liquidationThresholdPct,
  maxLtvPct: a.maxLtvPct,
  collateralValueUsd: a.collateralValueUsd,
  healthFactor: a.healthFactor,
  status: a.status,
}));

// ─── Shared Components ──────────────────────────────────────────────────────────

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

const PAGE_SIZE_OPTIONS = [6, 12, 24] as const;

function getHealthColor(hf: string): string {
  const val = parseFloat(hf);
  if (isNaN(val)) return 'text-muted-foreground';
  if (val === 0) return 'text-red-600';
  if (val < 1.5) return 'text-amber-600';
  return 'text-green-600';
}

function StatusBadge({ status }: { status: HealthStatus }) {
  const styles: Record<HealthStatus, string> = {
    healthy: 'bg-green-100 text-green-700',
    'at-risk': 'bg-amber-100 text-amber-700',
    liquidated: 'bg-red-100 text-red-700',
  };
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', styles[status])}>
      {status}
    </span>
  );
}

function FacilityStatusBadge({ status }: { status: FacilityStatus }) {
  const styles: Record<FacilityStatus, string> = {
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', styles[status])}>
      {status}
    </span>
  );
}

function PageNumbers({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('ellipsis');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center gap-0.5">
      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`e-${i}`} className="px-1.5 text-sm text-muted-foreground">
            ...
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`min-w-[28px] rounded-md px-1.5 py-1 text-sm font-medium transition-colors ${
              p === currentPage
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {p}
          </button>
        ),
      )}
    </div>
  );
}

function PaginationControls({
  page,
  setPage,
  pageSize,
  setPageSize,
  totalItems,
  label,
}: {
  page: number;
  setPage: (p: number | ((prev: number) => number)) => void;
  pageSize: number;
  setPageSize: (s: number) => void;
  totalItems: number;
  label: string;
}) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const rangeStart = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalItems);

  if (totalItems === 0) return null;

  return (
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm text-muted-foreground">
        Showing {rangeStart}–{rangeEnd} of {totalItems} {label}
      </span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground">Per page</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(1)}
              disabled={safePage === 1}
              className="rounded-md border border-border px-2 py-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              First
            </button>
            <button
              onClick={() => setPage((p: number) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
            </button>
            <PageNumbers currentPage={safePage} totalPages={totalPages} onPageChange={setPage} />
            <button
              onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              <Icon name="streamline:interface-arrows-button-right-arrow-right-keyboard" className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage === totalPages}
              className="rounded-md border border-border px-2 py-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              Last
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SearchAndViewControls({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  placeholder,
}: {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1 sm:flex-initial">
        <Icon name="streamline:search-visual" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          className={`${INPUT_CLASS} pl-9 sm:w-64`}
        />
      </div>
      <div className="flex rounded-lg border border-border">
        <button
          onClick={() => onViewModeChange('grid')}
          className={`rounded-l-lg p-2 transition-colors ${
            viewMode === 'grid'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="streamline:interface-edit-grid-grid-layout-layouts-module" className="h-4 w-4" />
        </button>
        <button
          onClick={() => onViewModeChange('list')}
          className={`rounded-r-lg p-2 transition-colors ${
            viewMode === 'list'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="streamline:task-list" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Unauthenticated View ───────────────────────────────────────────────────────

function PublicDashboard() {
  const { connect } = useWalletStore();
  const [cfSearch, setCfSearch] = useState('');
  const [cfStatusFilter, setCfStatusFilter] = useState<'all' | FacilityStatus>('all');
  const [cfViewMode, setCfViewMode] = useState<'grid' | 'list'>('grid');
  const [cfPage, setCfPage] = useState(1);
  const [cfPageSize, setCfPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  const filteredFacilities = useMemo(() => {
    let result = ALL_CREDIT_FACILITIES;
    if (cfStatusFilter !== 'all') {
      result = result.filter((f) => f.facilityStatus === cfStatusFilter);
    }
    if (cfSearch.trim()) {
      const q = cfSearch.toLowerCase();
      result = result.filter(
        (f) => f.name.toLowerCase().includes(q) || [...f.settlementTokens, ...f.collateralTokens].join(' ').toLowerCase().includes(q),
      );
    }
    return result;
  }, [cfSearch, cfStatusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<'all' | FacilityStatus, number> = { all: ALL_CREDIT_FACILITIES.length, active: 0, paused: 0 };
    for (const f of ALL_CREDIT_FACILITIES) counts[f.facilityStatus]++;
    return counts;
  }, []);

  const totalTvl = useMemo(
    () => ALL_CREDIT_FACILITIES.reduce((s, f) => s + parseFloat(f.tvl.replace(/[$M]/g, '')), 0),
    [],
  );
  const avgRate = useMemo(() => {
    const active = ALL_CREDIT_FACILITIES.filter((f) => f.facilityStatus === 'active');
    if (active.length === 0) return 0;
    return active.reduce((s, f) => s + parseFloat(f.interestRate), 0) / active.length;
  }, []);

  const totalPages = Math.ceil(filteredFacilities.length / cfPageSize);
  const safePage = Math.min(cfPage, Math.max(1, totalPages));
  const paginated = filteredFacilities.slice((safePage - 1) * cfPageSize, safePage * cfPageSize);

  const STATUS_FILTERS: { value: 'all' | FacilityStatus; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
  ];

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-background to-primary/5 p-8 sm:p-10">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Ascend Credit Platform</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Explore credit facilities, view market rates, and connect your wallet to create
          margin accounts, deposit collateral, borrow, lend, and manage your positions.
        </p>
        <Button className="mt-6" onClick={connect}>
          <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4" />
          Connect Wallet to Get Started
        </Button>
      </div>

      {/* Protocol Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Total Facilities</h3>
              <Icon name="streamline:bank" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold">{ALL_CREDIT_FACILITIES.length}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {statusCounts.active} active
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Total TVL</h3>
              <Icon name="streamline:money-cash-coins-stack-accounting-billing-payment-stack-cash-coins-currency-money-finance" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold">${totalTvl.toFixed(1)}M</p>
            <p className="mt-1 text-xs text-muted-foreground">Across all facilities</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Avg Interest Rate</h3>
              <Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold">{avgRate.toFixed(1)}%</p>
            <p className="mt-1 text-xs text-muted-foreground">Active facilities</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Protocol Status</h3>
              <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold text-green-600">Healthy</p>
            <p className="mt-1 text-xs text-muted-foreground">All systems operational</p>
          </CardContent>
        </Card>
      </div>

      {/* Available Credit Facilities */}
      <div>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg sm:text-xl font-semibold">
            Available Credit Facilities
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({filteredFacilities.length})
            </span>
          </h2>
          <SearchAndViewControls
            searchQuery={cfSearch}
            onSearchChange={(q) => { setCfSearch(q); setCfPage(1); }}
            viewMode={cfViewMode}
            onViewModeChange={setCfViewMode}
            placeholder="Search facilities..."
          />
        </div>

        <div className="mb-4 inline-flex rounded-lg bg-muted p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setCfStatusFilter(f.value); setCfPage(1); }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                cfStatusFilter === f.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
              <span className="ml-1.5 text-xs text-muted-foreground">{statusCounts[f.value]}</span>
            </button>
          ))}
        </div>

        {paginated.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">
              {cfSearch.trim() ? `No facilities matching "${cfSearch}"` : 'No facilities in this category.'}
            </p>
          </div>
        ) : cfViewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paginated.map((cf) => (
              <PublicFacilityCard key={cf.id} facility={cf} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginated.map((cf) => (
              <PublicFacilityRow key={cf.id} facility={cf} />
            ))}
          </div>
        )}

        <PaginationControls
          page={cfPage}
          setPage={setCfPage}
          pageSize={cfPageSize}
          setPageSize={setCfPageSize}
          totalItems={filteredFacilities.length}
          label="facilities"
        />
      </div>
    </div>
  );
}

function PublicFacilityCard({ facility }: { facility: CreditFacility }) {
  return (
    <div className="rounded-xl border border-border p-6 hover:bg-accent/50 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold">{facility.name}</h3>
          <p className="text-sm text-muted-foreground">{[...facility.collateralTokens, ...facility.settlementTokens].join(', ')}</p>
        </div>
        <FacilityStatusBadge status={facility.facilityStatus} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-y-3">
        <div>
          <div className="text-xs text-muted-foreground">TVL</div>
          <div className="text-lg font-bold">{facility.tvl}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Utilization</div>
          <div className="text-lg font-bold">{facility.utilization}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Interest Rate</div>
          <div className="text-lg font-bold text-primary">{facility.interestRate}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Max LTV</div>
          <div className="text-lg font-bold">{facility.maxLTV}</div>
        </div>
      </div>
    </div>
  );
}

function PublicFacilityRow({ facility }: { facility: CreditFacility }) {
  return (
    <div className="rounded-xl border border-border p-5 hover:bg-accent/50 transition-colors">
      <div className="hidden sm:flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="w-48 font-semibold">{facility.name}</h3>
          <span className="text-sm text-muted-foreground">{[...facility.collateralTokens, ...facility.settlementTokens].join(', ')}</span>
          <FacilityStatusBadge status={facility.facilityStatus} />
        </div>
        <div className="flex items-center gap-8 text-sm">
          <div>
            <span className="text-muted-foreground">TVL</span>{' '}
            <span className="ml-1 font-semibold">{facility.tvl}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Util</span>{' '}
            <span className="ml-1 font-semibold">{facility.utilization}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Rate</span>{' '}
            <span className="ml-1 font-semibold text-primary">{facility.interestRate}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Max LTV</span>{' '}
            <span className="ml-1 font-semibold">{facility.maxLTV}</span>
          </div>
        </div>
      </div>
      {/* Mobile stacked */}
      <div className="sm:hidden space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold">{facility.name}</h3>
            <p className="text-sm text-muted-foreground">{[...facility.collateralTokens, ...facility.settlementTokens].join(', ')}</p>
          </div>
          <FacilityStatusBadge status={facility.facilityStatus} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">TVL</span>{' '}
            <span className="ml-1 font-semibold">{facility.tvl}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Util</span>{' '}
            <span className="ml-1 font-semibold">{facility.utilization}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Rate</span>{' '}
            <span className="ml-1 font-semibold text-primary">{facility.interestRate}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Max LTV</span>{' '}
            <span className="ml-1 font-semibold">{facility.maxLTV}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Authenticated View ─────────────────────────────────────────────────────────

function AuthenticatedDashboard() {
  const navigate = useNavigate();
  // Credit Facilities state
  const [cfSearch, setCfSearch] = useState('');
  const [cfStatusFilter, setCfStatusFilter] = useState<'all' | HealthStatus>('all');
  const [cfViewMode, setCfViewMode] = useState<'grid' | 'list'>('grid');
  const [cfPage, setCfPage] = useState(1);
  const [cfPageSize, setCfPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  // Margin Accounts state
  const [maSearch, setMaSearch] = useState('');
  const [maStatusFilter, setMaStatusFilter] = useState<'all' | HealthStatus>('all');
  const [maViewMode, setMaViewMode] = useState<'grid' | 'list'>('grid');
  const [maPage, setMaPage] = useState(1);
  const [maPageSize, setMaPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  // Credit Facilities filtering
  const filteredCFs = useMemo(() => {
    let result = ALL_CREDIT_FACILITIES;
    if (cfStatusFilter !== 'all') {
      result = result.filter((f) => f.status === cfStatusFilter);
    }
    if (cfSearch.trim()) {
      const q = cfSearch.toLowerCase();
      result = result.filter(
        (f) => f.name.toLowerCase().includes(q) || [...f.settlementTokens, ...f.collateralTokens].join(' ').toLowerCase().includes(q),
      );
    }
    return result;
  }, [cfSearch, cfStatusFilter]);

  const cfStatusCounts = useMemo(() => {
    const counts: Record<'all' | HealthStatus, number> = { all: ALL_CREDIT_FACILITIES.length, healthy: 0, 'at-risk': 0, liquidated: 0 };
    for (const f of ALL_CREDIT_FACILITIES) counts[f.status]++;
    return counts;
  }, []);

  const cfTotalPages = Math.ceil(filteredCFs.length / cfPageSize);
  const cfSafePage = Math.min(cfPage, Math.max(1, cfTotalPages));
  const paginatedCFs = filteredCFs.slice((cfSafePage - 1) * cfPageSize, cfSafePage * cfPageSize);

  // Margin Accounts filtering
  const filteredMAs = useMemo(() => {
    let result = ALL_MARGIN_ACCOUNTS;
    if (maStatusFilter !== 'all') {
      result = result.filter((m) => m.status === maStatusFilter);
    }
    if (maSearch.trim()) {
      const q = maSearch.toLowerCase();
      result = result.filter(
        (m) => m.facilityName.toLowerCase().includes(q) || [...m.settlementTokens, ...m.collateralTokens].join(' ').toLowerCase().includes(q),
      );
    }
    return result;
  }, [maSearch, maStatusFilter]);

  const maStatusCounts = useMemo(() => {
    const counts: Record<'all' | HealthStatus, number> = { all: ALL_MARGIN_ACCOUNTS.length, healthy: 0, 'at-risk': 0, liquidated: 0 };
    for (const m of ALL_MARGIN_ACCOUNTS) counts[m.status]++;
    return counts;
  }, []);

  const maTotalPages = Math.ceil(filteredMAs.length / maPageSize);
  const maSafePage = Math.min(maPage, Math.max(1, maTotalPages));
  const paginatedMAs = filteredMAs.slice((maSafePage - 1) * maPageSize, maSafePage * maPageSize);

  const totalCollateral = useMemo(
    () => ALL_MARGIN_ACCOUNTS.reduce((s, m) => s + parseFloat(m.collateral.replace(/[$ ,]/g, '')), 0),
    [],
  );
  const totalBorrowed = useMemo(
    () => ALL_MARGIN_ACCOUNTS.reduce((s, m) => s + parseFloat(m.borrowed.replace(/[$ ,]/g, '')), 0),
    [],
  );
  const totalLendingValue = useMemo(
    () => CLIENT_LENDING_POOLS.reduce((s, p) => s + p.currentValueUsd, 0),
    [],
  );
  const totalEarnedYield = useMemo(
    () => CLIENT_LENDING_POOLS.reduce((s, p) => s + p.earnedYieldUsd, 0),
    [],
  );
  const netWorth = totalCollateral + totalLendingValue - totalBorrowed;

  const HEALTH_FILTERS: { value: 'all' | HealthStatus; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'healthy', label: 'Healthy' },
    { value: 'at-risk', label: 'At Risk' },
    { value: 'liquidated', label: 'Liquidated' },
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Portfolio Dashboard</h1>
        <p className="mt-1 text-muted-foreground">Your margin accounts and positions.</p>
      </div>


      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Net Worth</h3>
              <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold">${Math.floor(netWorth).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">Collateral + Lending &minus; Borrowed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Total Collateral</h3>
              <Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold">${totalCollateral.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Total Borrowed</h3>
              <Icon name="streamline:money-cash-bag-dollar-bag-payment-cash-money-finance" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold">${totalBorrowed.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Lending Value</h3>
              <Icon name="streamline:coins-stack" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold">${Math.floor(totalLendingValue).toLocaleString()}</p>
            <p className="text-xs text-ascend-success mt-1">+${Math.floor(totalEarnedYield).toLocaleString()} earned</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">Min Health Factor</h3>
              <Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-bold text-red-600">0.00</p>
          </CardContent>
        </Card>
      </div>

      {/* Wallet Balances Strip */}
      <div className="rounded-xl border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Wallet Balances</h2>
          </div>
          <button
            onClick={() => navigate('/profile#balances')}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            View all &rarr;
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CLIENT_WALLET_BALANCES).map(([token, balance]) => (
            <div
              key={token}
              className="inline-flex items-center gap-1.5 rounded-lg bg-muted/60 px-3 py-1.5 text-sm"
            >
              <span className="font-medium">{token}</span>
              <span className="text-muted-foreground">{balance}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lending Positions */}
      {CLIENT_LENDING_POOLS.length > 0 && (
        <LendingPositionsSection navigate={navigate} />
      )}

      {/* Your Credit Facilities */}
      <div>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg sm:text-xl font-semibold">
              Facilities with Active Accounts
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({filteredCFs.length})
              </span>
            </h2>
            <button
              onClick={() => navigate('/facility-access')}
              className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              title="Browse Credit Facilities"
            >
              <Icon name="streamline:compass-navigator" className="h-4 w-4" />
            </button>
          </div>
          <SearchAndViewControls
            searchQuery={cfSearch}
            onSearchChange={(q) => { setCfSearch(q); setCfPage(1); }}
            viewMode={cfViewMode}
            onViewModeChange={setCfViewMode}
            placeholder="Search facilities..."
          />
        </div>

        <div className="mb-4 inline-flex rounded-lg bg-muted p-1">
          {HEALTH_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setCfStatusFilter(f.value); setCfPage(1); }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                cfStatusFilter === f.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
              <span className="ml-1.5 text-xs text-muted-foreground">{cfStatusCounts[f.value]}</span>
            </button>
          ))}
        </div>

        {paginatedCFs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">
              {cfSearch.trim() ? `No facilities matching "${cfSearch}"` : 'No facilities in this category.'}
            </p>
          </div>
        ) : cfViewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paginatedCFs.map((cf) => (
              <CreditFacilityCard key={cf.id} facility={cf} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedCFs.map((cf) => (
              <CreditFacilityRow key={cf.id} facility={cf} />
            ))}
          </div>
        )}

        <PaginationControls
          page={cfPage}
          setPage={setCfPage}
          pageSize={cfPageSize}
          setPageSize={setCfPageSize}
          totalItems={filteredCFs.length}
          label="facilities"
        />
      </div>

      {/* Margin Accounts */}
      <div>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg sm:text-xl font-semibold">
            Margin Accounts
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({filteredMAs.length})
            </span>
          </h2>
          <SearchAndViewControls
            searchQuery={maSearch}
            onSearchChange={(q) => { setMaSearch(q); setMaPage(1); }}
            viewMode={maViewMode}
            onViewModeChange={setMaViewMode}
            placeholder="Search accounts..."
          />
        </div>

        <div className="mb-4 inline-flex rounded-lg bg-muted p-1">
          {HEALTH_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setMaStatusFilter(f.value); setMaPage(1); }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                maStatusFilter === f.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
              <span className="ml-1.5 text-xs text-muted-foreground">{maStatusCounts[f.value]}</span>
            </button>
          ))}
        </div>

        {paginatedMAs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">
              {maSearch.trim() ? `No accounts matching "${maSearch}"` : 'No accounts in this category.'}
            </p>
          </div>
        ) : maViewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paginatedMAs.map((ma) => (
              <MarginAccountCard key={ma.id} account={ma} onOpenDetail={(a) => navigate(`/margin-accounts/${a.id}`)} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedMAs.map((ma) => (
              <MarginAccountRow key={ma.id} account={ma} onOpenDetail={(a) => navigate(`/margin-accounts/${a.id}`)} />
            ))}
          </div>
        )}

        <PaginationControls
          page={maPage}
          setPage={setMaPage}
          pageSize={maPageSize}
          setPageSize={setMaPageSize}
          totalItems={filteredMAs.length}
          label="accounts"
        />
      </div>

    </div>
  );
}

// ─── Lending Positions Section ───────────────────────────────────────────────────

function LendingPositionsSection({ navigate }: { navigate: (path: string) => void }) {
  const [lpSearch, setLpSearch] = useState('');
  const [lpViewMode, setLpViewMode] = useState<'grid' | 'list'>('grid');
  const [lpPage, setLpPage] = useState(1);
  const [lpPageSize, setLpPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  const filteredLPs = useMemo(() => {
    if (!lpSearch.trim()) return CLIENT_LENDING_POOLS;
    const q = lpSearch.toLowerCase();
    return CLIENT_LENDING_POOLS.filter((p) => p.name.toLowerCase().includes(q));
  }, [lpSearch]);

  const lpTotalPages = Math.ceil(filteredLPs.length / lpPageSize);
  const lpSafePage = Math.min(lpPage, Math.max(1, lpTotalPages));
  const paginatedLPs = filteredLPs.slice((lpSafePage - 1) * lpPageSize, lpSafePage * lpPageSize);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg sm:text-xl font-semibold">
          Lending Positions
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            ({filteredLPs.length})
          </span>
        </h2>
        <SearchAndViewControls
          searchQuery={lpSearch}
          onSearchChange={(q) => { setLpSearch(q); setLpPage(1); }}
          viewMode={lpViewMode}
          onViewModeChange={setLpViewMode}
          placeholder="Search pools..."
        />
      </div>

      {paginatedLPs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <p className="text-sm text-muted-foreground">
            {lpSearch.trim() ? `No pools matching "${lpSearch}"` : 'No lending positions.'}
          </p>
        </div>
      ) : lpViewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {paginatedLPs.map((pool) => (
            <LendingPositionCard key={pool.id} pool={pool} onClick={() => navigate(`/lending/${pool.id}`)} />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedLPs.map((pool) => (
            <LendingPositionRow key={pool.id} pool={pool} onClick={() => navigate(`/lending/${pool.id}`)} />
          ))}
        </div>
      )}

      <PaginationControls
        page={lpPage}
        setPage={setLpPage}
        pageSize={lpPageSize}
        setPageSize={setLpPageSize}
        totalItems={filteredLPs.length}
        label="positions"
      />
    </div>
  );
}

function LendingPositionCard({ pool, onClick }: { pool: typeof CLIENT_LENDING_POOLS[number]; onClick: () => void }) {
  return (
    <div
      className="cursor-pointer rounded-xl border border-border p-5 hover:bg-accent/50 transition-colors"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <h3 className="font-semibold">{pool.name}</h3>
        <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
          {pool.apy}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-muted-foreground">Current Value</div>
          <div className="text-lg font-bold">{pool.currentValue}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Earned Yield</div>
          <div className="text-lg font-bold text-ascend-success">{pool.earnedYield}</div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
        <span>Deposited <span className="font-medium text-foreground">{pool.deposited}</span></span>
        <span>TVL <span className="font-medium text-foreground">{pool.tvl}</span></span>
      </div>
    </div>
  );
}

function LendingPositionRow({ pool, onClick }: { pool: typeof CLIENT_LENDING_POOLS[number]; onClick: () => void }) {
  return (
    <div
      className="cursor-pointer rounded-xl border border-border p-5 hover:bg-accent/50 transition-colors"
      onClick={onClick}
    >
      <div className="hidden sm:flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="w-48 font-semibold">{pool.name}</h3>
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            {pool.apy}
          </span>
        </div>
        <div className="flex items-center gap-8 text-sm">
          <div>
            <span className="text-muted-foreground">Deposited</span>{' '}
            <span className="ml-1 font-semibold">{pool.deposited}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Value</span>{' '}
            <span className="ml-1 font-semibold">{pool.currentValue}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Yield</span>{' '}
            <span className="ml-1 font-semibold text-ascend-success">{pool.earnedYield}</span>
          </div>
          <div>
            <span className="text-muted-foreground">TVL</span>{' '}
            <span className="ml-1 font-semibold">{pool.tvl}</span>
          </div>
        </div>
      </div>
      <div className="sm:hidden space-y-3">
        <div className="flex items-start justify-between">
          <h3 className="font-semibold">{pool.name}</h3>
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            {pool.apy}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Deposited</span>{' '}
            <span className="ml-1 font-semibold">{pool.deposited}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Value</span>{' '}
            <span className="ml-1 font-semibold">{pool.currentValue}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Yield</span>{' '}
            <span className="ml-1 font-semibold text-ascend-success">{pool.earnedYield}</span>
          </div>
          <div>
            <span className="text-muted-foreground">TVL</span>{' '}
            <span className="ml-1 font-semibold">{pool.tvl}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Authenticated Card/Row Components ──────────────────────────────────────────

function CreditFacilityCard({ facility }: { facility: CreditFacility }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/credit-facilities/${facility.id}`)}
      className="cursor-pointer rounded-xl border border-border p-6 hover:bg-accent/50 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold">{facility.name}</h3>
          <p className="text-sm text-muted-foreground">{[...facility.collateralTokens, ...facility.settlementTokens].join(', ')}</p>
        </div>
        <StatusBadge status={facility.status} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-y-3">
        <div>
          <div className="text-xs text-muted-foreground">Collateral</div>
          <div className="text-lg font-bold">{facility.collateral}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Borrowed</div>
          <div className="text-lg font-bold">{facility.borrowed}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Health Factor</div>
          <div className={cn('text-lg font-bold', getHealthColor(facility.healthFactor))}>
            {facility.healthFactor}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Interest Rate</div>
          <div className="text-lg font-bold text-primary">{facility.interestRate}</div>
        </div>
      </div>
    </div>
  );
}

function CreditFacilityRow({ facility }: { facility: CreditFacility }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/credit-facilities/${facility.id}`)}
      className="cursor-pointer rounded-xl border border-border p-5 hover:bg-accent/50 transition-colors"
    >
      <div className="hidden sm:flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="w-48 font-semibold">{facility.name}</h3>
          <span className="text-sm text-muted-foreground">{[...facility.collateralTokens, ...facility.settlementTokens].join(', ')}</span>
          <StatusBadge status={facility.status} />
        </div>
        <div className="flex items-center gap-8 text-sm">
          <div>
            <span className="text-muted-foreground">Collateral</span>{' '}
            <span className="ml-1 font-semibold">{facility.collateral}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Borrowed</span>{' '}
            <span className="ml-1 font-semibold">{facility.borrowed}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Health</span>{' '}
            <span className={cn('ml-1 font-semibold', getHealthColor(facility.healthFactor))}>
              {facility.healthFactor}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Rate</span>{' '}
            <span className="ml-1 font-semibold text-primary">{facility.interestRate}</span>
          </div>
        </div>
      </div>
      <div className="sm:hidden space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold">{facility.name}</h3>
            <p className="text-sm text-muted-foreground">{[...facility.collateralTokens, ...facility.settlementTokens].join(', ')}</p>
          </div>
          <StatusBadge status={facility.status} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Collateral</span>{' '}
            <span className="ml-1 font-semibold">{facility.collateral}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Borrowed</span>{' '}
            <span className="ml-1 font-semibold">{facility.borrowed}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Health</span>{' '}
            <span className={cn('ml-1 font-semibold', getHealthColor(facility.healthFactor))}>
              {facility.healthFactor}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Rate</span>{' '}
            <span className="ml-1 font-semibold text-primary">{facility.interestRate}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarginAccountCard({ account, onOpenDetail }: { account: MarginAccount; onOpenDetail: (account: MarginAccount) => void }) {
  const totalOwed = account.borrowedUsd + account.interestAccruedUsd;

  return (
    <div
      className="rounded-xl border border-border p-5 hover:bg-accent/50 transition-colors cursor-pointer"
      onClick={() => onOpenDetail(account)}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold">{account.facilityName}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{[...account.collateralTokens, ...account.settlementTokens].join(', ')}</p>
        </div>
        <StatusBadge status={account.status} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-muted-foreground">Total Owed</div>
          <div className="text-lg font-bold">${totalOwed.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Health Factor</div>
          <div className={cn('text-lg font-bold', getHealthColor(account.healthFactor))}>
            {account.healthFactor}
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
        <span>Collateral <span className="font-medium text-foreground">{account.collateral}</span></span>
        <span>Borrowed <span className="font-medium text-foreground">{account.borrowed}</span></span>
      </div>
    </div>
  );
}

function MarginAccountRow({ account, onOpenDetail }: { account: MarginAccount; onOpenDetail: (account: MarginAccount) => void }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-border p-5 hover:bg-accent/50 transition-colors cursor-pointer" onClick={() => onOpenDetail(account)}>
      <div className="hidden sm:flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="w-48 font-semibold">{account.facilityName}</h3>
          <span className="text-sm text-muted-foreground">{[...account.collateralTokens, ...account.settlementTokens].join(', ')}</span>
          <StatusBadge status={account.status} />
        </div>
        <div className="flex items-center gap-6 text-sm">
          <div>
            <span className="text-muted-foreground">Collateral</span>{' '}
            <span className="ml-1 font-semibold">{account.collateral}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Borrowed</span>{' '}
            <span className="ml-1 font-semibold">{account.borrowed}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Interest</span>{' '}
            <span className="ml-1 font-semibold">{account.interestAccrued}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Health</span>{' '}
            <span className={cn('ml-1 font-semibold', getHealthColor(account.healthFactor))}>
              {account.healthFactor}
            </span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/deposit?facilityId=${account.facilityId}`); }}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title="Deposit"
            >
              <Icon name="streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom" className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/withdraw?accountId=${account.id}`); }}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title="Withdraw"
            >
              <Icon name="streamline:interface-arrows-button-to-top-arrow-up-line-to-top" className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/borrow?accountId=${account.id}`); }}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title="Borrow"
            >
              <Icon name="streamline:bank" className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/repay?accountId=${account.id}`); }}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title="Repay"
            >
              <Icon name="streamline:interface-arrows-synchronize-arrows-loading-load-sync-synchronize-arrow-reload" className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile stacked */}
      <div className="sm:hidden space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold">{account.facilityName}</h3>
            <p className="text-sm text-muted-foreground">{[...account.collateralTokens, ...account.settlementTokens].join(', ')}</p>
          </div>
          <StatusBadge status={account.status} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Collateral</span>{' '}
            <span className="ml-1 font-semibold">{account.collateral}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Borrowed</span>{' '}
            <span className="ml-1 font-semibold">{account.borrowed}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Interest</span>{' '}
            <span className="ml-1 font-semibold">{account.interestAccrued}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Health</span>{' '}
            <span className={cn('ml-1 font-semibold', getHealthColor(account.healthFactor))}>
              {account.healthFactor}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate(`/deposit?facilityId=${account.facilityId}`)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            <Icon name="streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom" className="h-3.5 w-3.5" />
            Deposit
          </button>
          <button
            onClick={() => navigate(`/withdraw?accountId=${account.id}`)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            <Icon name="streamline:interface-arrows-button-to-top-arrow-up-line-to-top" className="h-3.5 w-3.5" />
            Withdraw
          </button>
          <button
            onClick={() => navigate(`/borrow?accountId=${account.id}`)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            <Icon name="streamline:bank" className="h-3.5 w-3.5" />
            Borrow
          </button>
          <button
            onClick={() => navigate(`/repay?accountId=${account.id}`)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            <Icon name="streamline:interface-arrows-synchronize-arrows-loading-load-sync-synchronize-arrow-reload" className="h-3.5 w-3.5" />
            Repay
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────────

export function PortfolioDashboardPage() {
  const { isConnected } = useWalletStore();
  return isConnected ? <AuthenticatedDashboard /> : <PublicDashboard />;
}
